import React, { useState } from "react";
import { db } from "../firebaseConfig";
import { doc, setDoc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import studentIcon from "../assets/student.png";
import classes from "./LoginPage.module.css";
import { Eye, EyeOff } from "lucide-react"; // lightweight icon set

export default function SignupForm() {
    const [showPassword, setShowPassword] = useState(false);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        stdRegNumber: "",
        department: "",
        program: "",
        semester: "",
        section: "",
        username: "",
        password: ""
    });

    const [prefix, setPrefix] = useState("CS-");

    const [regNumberError, setRegNumberError] = useState("");
    const [isRegNumberValid, setIsRegNumberValid] = useState(false);
    const [usernameError, setUsernameError] = useState("");
    const [isUsernameValid, setIsUsernameValid] = useState(false);

    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "username") {
            setFormData((prev) => ({ ...prev, [name]: value.replace(/\s+$/, "") }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const checkUsername = async () => {
        if (!formData.username.trim()) return;
        try {
            const q = query(collection(db, "students"), where("username", "==", formData.username));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setUsernameError("This username is already taken.");
                setIsUsernameValid(false);
            } else {
                setUsernameError("");
                setIsUsernameValid(true);
            }
        } catch (error) {
            console.error("Error checking username:", error);
            setUsernameError("Error checking username.");
            setIsUsernameValid(false);
        }
    };

    const checkRegNumber = async () => {
        if (!formData.stdRegNumber.trim()) return;
        try {
            const regWithPrefix = prefix + formData.stdRegNumber;
            const docRef = doc(db, "students", regWithPrefix);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setRegNumberError("This registration number is already registered.");
                setIsRegNumberValid(false);
            } else {
                setRegNumberError("");
                setIsRegNumberValid(true);
            }
        } catch (error) {
            console.error("Error checking reg number:", error);
            setRegNumberError("Error checking registration number.");
            setIsRegNumberValid(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isRegNumberValid || !isUsernameValid) {
            alert("Please enter unique registration number and username before signing up.");
            return;
        }

        const regWithPrefix = prefix + formData.stdRegNumber;

        const fullData = {
            ...formData,
            stdRegNumber: regWithPrefix,
            gender: " ",
            dateOfBirth: " ",
            cnic: " ",
            contactNumber: " ",
            email: " ",
            fatherName: " ",
            fatherOccupation: " ",
            address: {
                city: " ",
                houseNo: " ",
                street: " ",
                town: " "
            },
            feeSummary: [],
            prevAcademicRecord: []
        };

        try {
            await setDoc(doc(db, "students", regWithPrefix), fullData);
            alert("Student registered successfully!");
            navigate("/login");
        } catch (error) {
            console.error("Error registering student: ", error);
            alert("Failed to register student.");
        }
    };

    return (
        <div className={classes.container}>
            <header className={classes.loginHeader}>
                <img src={studentIcon} alt="icon-of-students" />
                <div className={classes.myHead}>
                    <h1>National University of Modern Languages</h1>
                    <h3>LAHORE CAMPUS</h3>
                </div>
            </header>

            <div className={classes.formWrapper}>
                <h2 className={classes.heading}>STUDENT SIGNUP</h2>
                <form onSubmit={handleSubmit} className={classes.form}>
                    <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className={classes.input} />
                    <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className={classes.input} />

                    {/* Roll Number with Prefix */}
                    <div className={classes.inputWithPrefix}>
                        <select
                            value={prefix}
                            onChange={(e) => setPrefix(e.target.value)}
                            className={classes.prefixDropdown}
                        >
                            <option value="CS-">CS-</option>
                            <option value="L-BSCS">L-BSCS</option>
                        </select>
                        <input
                            name="stdRegNumber"
                            value={formData.stdRegNumber}
                            onChange={handleChange}
                            onBlur={checkRegNumber}
                            placeholder="Roll Number"
                            required
                            className={classes.input}
                            style={{ flex: 1 }}
                        />
                    </div>
                    {regNumberError && (
                        <p style={{ color: "red", fontSize: "0.85rem", marginTop: "-10px" }}>{regNumberError}</p>
                    )}

                    <select name="department" value={formData.department} onChange={handleChange} required className={classes.input}>
                        <option value="">Select Department</option>
                        <option value="CS">Computer Science</option>
                    </select>

                    <select name="program" value={formData.program} onChange={handleChange} required className={classes.input}>
                        <option value="">Select Program</option>
                        <option value="BCS">BS Computer Science</option>
                        <option value="BSE">BS Software Engineering</option>
                    </select>

                    <select name="section" value={formData.section} onChange={handleChange} required className={classes.input}>
                        <option value="">Select Section</option>
                        <option value="1">A</option>
                        <option value="2">B</option>
                        <option value="1">none</option>

                    </select>

                    <select name="semester" value={formData.semester} onChange={handleChange} required className={classes.input}>
                        <option value="">Select Semester</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                            <option key={num} value={num}>{num}</option>
                        ))}
                    </select>

                    <input
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        onBlur={checkUsername}
                        placeholder="Username"
                        required
                        className={classes.input}
                    />
                    {usernameError && (
                        <p style={{ color: "red", fontSize: "0.85rem", marginTop: "-10px" }}>
                            {usernameError}
                        </p>
                    )}

                    <div className={classes.passwordWrapper}>
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Password"
                            required
                            className={classes.input}
                        />
                        <span
                            className={classes.passwordToggle}
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </span>
                    </div>

                    <div className={classes.buttonGroup}>
                        <button type="button" className={`${classes.button} ${classes.signupButton}`} onClick={() => navigate("/login")}>
                            BACK TO LOGIN
                        </button>
                        <button
                            type="submit"
                            className={classes.button}
                            disabled={!isRegNumberValid || !isUsernameValid}
                        >
                            SIGN UP
                        </button>
                    </div>
                </form>
            </div>

            <footer className={classes.loginFooter}>
                <p>All rights reserved.</p>
                <p>
                    Developed by{" "}
                    <a href="https://github.com/xeeshan-zs" target="_self" rel="noopener noreferrer">
                        Zeeshan Sarfraz
                    </a>
                </p>
            </footer>
        </div>
    );
}
